import { useState, useRef, useCallback, useEffect } from "react";
import { t } from "../i18n";

// ─── Constants ───
const INITIAL_MONTAGE = {
    montage_id: null,
    title: "",
    description: "",
    aspect_ratio: "16:9",
    resolution: "1080p",
    target_duration: 30,
    status: "draft",
    json_data: {
        segments: [],
        music: null,
        total_duration: 0,
        reference_media: []
    }
};

/** Sync ?id= in the URL bar without causing a page reload. */
function setMontageIdInUrl(montageId) {
    const params = new URLSearchParams(window.location.search);
    if (montageId) {
        params.set("id", montageId);
    } else {
        params.delete("id");
    }
    const query = params.toString();
    const url = query ? `${window.location.pathname}?${query}` : window.location.pathname;
    window.history.replaceState({}, "", url);
}

/**
 * useMontage – Manages all montage CRUD, auto-save, media picker consumption,
 * and segment manipulation logic.
 *
 * @param {function} toast – Toast notification helper.
 * @returns {object} Montage state, refs, and all handlers.
 */
export default function useMontage(toast) {
    // ─── Core state ───
    const [montage, setMontage] = useState(INITIAL_MONTAGE);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [currentStep, setCurrentStep] = useState("storyboard");

    // ─── Project / list state ───
    const [activeProjectId, setActiveProjectId] = useState("");
    const [montagesList, setMontagesList] = useState([]);
    const [loadingList, setLoadingList] = useState(false);

    // ─── Generation state ───
    const [imageQuality, setImageQuality] = useState("medium");
    const [generatingImages, setGeneratingImages] = useState(false);
    const [videoQuality, setVideoQuality] = useState("medium");
    const [generatingVideos, setGeneratingVideos] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [harmonizing, setHarmonizing] = useState(false);
    const [regeneratingSegment, setRegeneratingSegment] = useState(null);
    const [enhancingPrompts, setEnhancingPrompts] = useState(false);

    // ─── Drag & drop state ───
    const [draggedSegmentIndex, setDraggedSegmentIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    // ─── Refs ───
    const autoSaveTimerRef = useRef(null);
    const lastSavedRef = useRef(null);

    // ─── Load active project ID from sessionStorage ───
    useEffect(() => {
        try {
            const storedProjectId = window.sessionStorage.getItem("contentos_active_project_id") || "";
            setActiveProjectId(storedProjectId);
        } catch (err) {
            console.error("Error loading project ID:", err);
        }
    }, []);

    // ─── Load montages list ───
    useEffect(() => {
        if (!activeProjectId) return;
        let isMounted = true;
        setLoadingList(true);

        fetch(`/api/fast/montage/list?project_id=${encodeURIComponent(activeProjectId)}`)
            .then(res => res.json())
            .then(data => {
                if (!isMounted) return;
                setMontagesList(data.montages || []);
            })
            .catch(err => {
                if (!isMounted) return;
                console.error("Error loading montages:", err);
            })
            .finally(() => {
                if (isMounted) setLoadingList(false);
            });

        return () => { isMounted = false; };
    }, [activeProjectId]);

    // ─── Auto-load montage from ?id= URL param on mount ───
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const urlMontageId = params.get("id");
        if (urlMontageId && !montage.montage_id) {
            handleLoad(urlMontageId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ─── Auto-save effect ───
    useEffect(() => {
        if (!montage.montage_id) return;
        if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);

        autoSaveTimerRef.current = setTimeout(() => {
            handleSave(true);
        }, 30000);

        return () => {
            if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
        };
    }, [montage]);

    // ─── Media picker consumption ───
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get("media_picker_done") !== "1") return;

        const urlMontageId = params.get("id");
        if (urlMontageId && montage.montage_id !== urlMontageId) return;

        const target = params.get("target") || "";

        const clearSelection = async () => {
            try {
                await fetch("/api/fast/media/selection", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ image_paths: [], target })
                });
            } catch {
                // Ignore errors clearing selection.
            }
        };

        const segMatch = target.match(/^segment_(start|end)_(.+)$/);
        const isSegmentPick = !!segMatch;
        const isRefPick = target === "montage_ref";

        if (!isRefPick && !isSegmentPick) return;

        let handled = false;
        try {
            const raw = window.sessionStorage.getItem("contentos_media_picker_payload");
            if (raw) {
                const payload = JSON.parse(raw);
                if (payload?.target === target && Array.isArray(payload.images) && payload.images.length > 0) {
                    if (isRefPick) {
                        const urls = payload.images;
                        const versions = Array.isArray(payload.versions) ? payload.versions : [];
                        const mapped = urls.map((url) => {
                            const match = versions.find((item) => item.url === url) || {};
                            return {
                                url,
                                thumbnail_url: match.thumbnail_url || match.thumbnailUrl || "",
                                mediaItemId: match.media_item_id || match.mediaItemId || "",
                                mediaVersionId: match.media_version_id || match.mediaVersionId || match.version_id || "",
                                assetId: match.asset_id || match.assetId || ""
                            };
                        });
                        setMontage(prev => {
                            const current = prev.json_data.reference_media || [];
                            const currentUrls = new Set(current.map(m => m.url));
                            const newItems = mapped.filter(m => !currentUrls.has(m.url));
                            return {
                                ...prev,
                                json_data: { ...prev.json_data, reference_media: [...current, ...newItems] }
                            };
                        });
                    } else if (isSegmentPick) {
                        const imageType = segMatch[1] === "start" ? "start_image" : "end_image";
                        const segmentId = segMatch[2];
                        const pickedUrl = payload.images[0];
                        setMontage(prev => {
                            const updatedSegments = (prev.json_data.segments || []).map(seg => {
                                if (String(seg.segment_id) === String(segmentId)) {
                                    return { ...seg, [imageType]: { ...seg[imageType], url: pickedUrl, status: "completed", type: "library" } };
                                }
                                return seg;
                            });
                            return { ...prev, json_data: { ...prev.json_data, segments: updatedSegments } };
                        });
                    }
                    window.sessionStorage.removeItem("contentos_media_picker_payload");
                    handled = true;
                }
            }
        } catch {
            // Ignore storage errors.
        }

        async function consumeSelection() {
            try {
                if (handled) return;
                const res = await fetch("/api/fast/media/selection");
                if (!res.ok) return;
                const data = await res.json();
                const selection = data.selection || {};
                if (selection.target && selection.target !== target) return;
                const urls = Array.isArray(selection.images) ? selection.images : [];
                if (isRefPick) {
                    const mapped = urls.map((url) => ({ url }));
                    setMontage(prev => {
                        const current = prev.json_data.reference_media || [];
                        const currentUrls = new Set(current.map(m => m.url));
                        const newItems = mapped.filter(m => !currentUrls.has(m.url));
                        return {
                            ...prev,
                            json_data: { ...prev.json_data, reference_media: [...current, ...newItems] }
                        };
                    });
                } else if (isSegmentPick && urls.length > 0) {
                    const imageType = segMatch[1] === "start" ? "start_image" : "end_image";
                    const segmentId = segMatch[2];
                    const pickedUrl = urls[0];
                    setMontage(prev => {
                        const updatedSegments = (prev.json_data.segments || []).map(seg => {
                            if (String(seg.segment_id) === String(segmentId)) {
                                return { ...seg, [imageType]: { ...seg[imageType], url: pickedUrl, status: "completed", type: "library" } };
                            }
                            return seg;
                        });
                        return { ...prev, json_data: { ...prev.json_data, segments: updatedSegments } };
                    });
                }
            } finally {
                await clearSelection();
                params.delete("media_picker_done");
                params.delete("target");
                const base = window.location.pathname;
                const query = params.toString();
                window.history.replaceState({}, "", query ? `${base}?${query}` : base);
            }
        }

        consumeSelection();
    }, [montage.montage_id]);

    // ──────────────────────────────────────────────────
    // CRUD handlers
    // ──────────────────────────────────────────────────

    const saveMontageSnapshot = useCallback(async (snapshot, isAutoSave = false, label = "") => {
        if (!snapshot?.montage_id) return;

        const currentState = JSON.stringify(snapshot);
        if (currentState === lastSavedRef.current) return;

        setSaving(true);
        if (!isAutoSave) setError("");

        try {
            const res = await fetch(`/api/fast/montage/${snapshot.montage_id}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    title: snapshot.title,
                    description: snapshot.description,
                    json_data: snapshot.json_data,
                    status: snapshot.status
                })
            });

            const data = await res.json();
            if (!data.ok) throw new Error(data.error || t("montage.err.save"));

            lastSavedRef.current = currentState;
            const message = label || (isAutoSave ? t("montage.toast.autoSave") : t("montage.toast.saved"));
            toast(message, "success");
        } catch (err) {
            toast(err.message || t("montage.err.save"), "error");
            console.error("Save error:", err);
        } finally {
            setSaving(false);
        }
    }, [toast]);

    const handleSave = useCallback(async (isAutoSave = false) => {
        if (!montage.montage_id) return;
        await saveMontageSnapshot(montage, isAutoSave);
    }, [montage, saveMontageSnapshot]);

    const handleCreate = useCallback(async () => {
        if (!montage.title.trim()) {
            setError(t("montage.err.titleRequired"));
            return;
        }

        setLoading(true);
        setError("");

        try {
            const res = await fetch("/api/fast/montage/create", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    project_id: activeProjectId || null,
                    title: montage.title,
                    description: montage.description,
                    aspect_ratio: montage.aspect_ratio,
                    resolution: montage.resolution
                })
            });

            const data = await res.json();
            if (!data.ok) throw new Error(data.error || t("montage.err.create"));

            setMontage(data.montage);
            lastSavedRef.current = JSON.stringify(data.montage);
            setMontageIdInUrl(data.montage.montage_id);
            toast(t("montage.toast.created"), "success");

            if (activeProjectId) {
                const listRes = await fetch(`/api/fast/montage/list?project_id=${encodeURIComponent(activeProjectId)}`);
                const listData = await listRes.json();
                setMontagesList(listData.montages || []);
            }
        } catch (err) {
            setError(err.message || t("montage.err.create"));
        } finally {
            setLoading(false);
        }
    }, [montage.title, montage.description, montage.aspect_ratio, montage.resolution, activeProjectId, toast]);

    const handleLoad = useCallback(async (montageId) => {
        setLoading(true);
        setError("");

        try {
            const res = await fetch(`/api/fast/montage/${montageId}`);
            const data = await res.json();
            if (!data.ok) throw new Error(data.error || t("montage.err.load"));

            setMontage(data.montage);
            lastSavedRef.current = JSON.stringify(data.montage);
            setMontageIdInUrl(data.montage.montage_id);
        } catch (err) {
            setError(err.message || t("montage.err.load"));
        } finally {
            setLoading(false);
        }
    }, []);

    const handleDelete = useCallback(async (montageId) => {
        if (!confirm(t("montage.confirm.delete"))) return;

        try {
            const res = await fetch(`/api/fast/montage/${montageId}`, { method: "DELETE" });
            const data = await res.json();
            if (!data.ok) throw new Error(data.error || t("montage.err.delete"));

            if (activeProjectId) {
                const listRes = await fetch(`/api/fast/montage/list?project_id=${encodeURIComponent(activeProjectId)}`);
                const listData = await listRes.json();
                setMontagesList(listData.montages || []);
            }

            if (montageId === montage.montage_id) {
                setMontage(INITIAL_MONTAGE);
                setCurrentStep("storyboard");
                setMontageIdInUrl(null);
            }
        } catch (err) {
            setError(err.message || t("montage.err.delete"));
        }
    }, [activeProjectId, montage.montage_id]);

    // ──────────────────────────────────────────────────
    // Field updaters
    // ──────────────────────────────────────────────────

    const updateMontage = useCallback((field, value) => {
        setMontage(prev => {
            const next = { ...prev, [field]: value };
            if (field === "aspect_ratio" || field === "resolution") {
                next.json_data = { ...prev.json_data, [field]: value };
            }
            return next;
        });
    }, []);

    const handleReferenceChange = useCallback((items) => {
        setMontage(prev => ({
            ...prev,
            json_data: { ...prev.json_data, reference_media: items }
        }));
    }, []);

    const handleAddReferenceFromSegment = useCallback((url) => {
        if (!url) return;
        setMontage(prev => {
            const current = prev.json_data.reference_media || [];
            if (current.some((entry) => entry.url === url)) return prev;
            const next = [{ url }, ...current].slice(0, 8);
            return {
                ...prev,
                json_data: { ...prev.json_data, reference_media: next }
            };
        });
        toast(t("montage.toast.refAdded"), "success");
    }, [toast]);

    const updateJsonData = useCallback((field, value) => {
        setMontage(prev => ({
            ...prev,
            json_data: { ...prev.json_data, [field]: value }
        }));
    }, []);

    // ──────────────────────────────────────────────────
    // Segment manipulation
    // ──────────────────────────────────────────────────

    const handleUpdateSegment = useCallback((index, updatedSegment) => {
        setMontage(prev => {
            const newSegments = [...prev.json_data.segments];
            newSegments[index] = updatedSegment;
            const totalDuration = newSegments.reduce((sum, seg) => sum + (seg.duration || 0), 0);
            return {
                ...prev,
                json_data: { ...prev.json_data, segments: newSegments, total_duration: totalDuration }
            };
        });
    }, []);

    const handleSaveSegment = useCallback((index, updatedSegment) => {
        setMontage(prev => {
            const newSegments = [...prev.json_data.segments];
            newSegments[index] = updatedSegment;
            const totalDuration = newSegments.reduce((sum, seg) => sum + (seg.duration || 0), 0);
            const next = {
                ...prev,
                json_data: { ...prev.json_data, segments: newSegments, total_duration: totalDuration }
            };
            saveMontageSnapshot(next, false, t("montage.toast.segmentSaved"));
            return next;
        });
    }, [saveMontageSnapshot]);

    const handleDeleteSegment = useCallback((index) => {
        setMontage(prev => {
            const newSegments = prev.json_data.segments.filter((_, i) => i !== index);
            const totalDuration = newSegments.reduce((sum, seg) => sum + (seg.duration || 0), 0);
            return {
                ...prev,
                json_data: { ...prev.json_data, segments: newSegments, total_duration: totalDuration }
            };
        });
    }, []);

    const handleAddSegment = useCallback(() => {
        const newSegmentNumber = montage.json_data.segments.length + 1;
        const newSegment = {
            segment_id: `seg_${String(newSegmentNumber).padStart(3, '0')}`,
            order: newSegmentNumber,
            duration: 10,
            prompt: { text: "", enhanced: "", tags: [] },
            start_image: { type: "to_generate", prompt: "", url: null, status: "pending" },
            end_image: { type: "to_generate", prompt: "", url: null, status: "pending" },
            video: { url: null, status: "pending" },
            transition_in: { type: "crossfade", duration: 0.5 },
            transition_out: { type: "crossfade", duration: 1.0 },
            overlays: []
        };

        setMontage(prev => {
            const newSegments = [...prev.json_data.segments, newSegment];
            const totalDuration = newSegments.reduce((sum, seg) => sum + (seg.duration || 0), 0);
            return {
                ...prev,
                json_data: { ...prev.json_data, segments: newSegments, total_duration: totalDuration }
            };
        });
    }, [montage.json_data.segments.length]);

    // ──────────────────────────────────────────────────
    // Generation handlers
    // ──────────────────────────────────────────────────

    const handleGenerateStoryboard = useCallback(async () => {
        if (!montage.description.trim()) {
            setError(t("montage.err.descRequired"));
            return;
        }

        setLoading(true);
        setError("");

        try {
            const segmentCount = Math.max(2, Math.round(montage.target_duration / 10));

            const res = await fetch("/api/fast/montage/storyboard/generate", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    description: montage.description,
                    target_duration: montage.target_duration,
                    segment_count: segmentCount,
                    aspect_ratio: montage.aspect_ratio
                })
            });

            const data = await res.json();
            if (!data.ok) throw new Error(data.error || t("montage.err.storyboard"));

            const segments = data.segments || [];
            const totalDuration = segments.reduce((sum, seg) => sum + (seg.duration || 0), 0);

            let updatedMontage;
            setMontage(prev => {
                updatedMontage = {
                    ...prev,
                    json_data: { ...prev.json_data, segments, total_duration: totalDuration }
                };
                return updatedMontage;
            });

            toast(`${t("montage.toast.storyboardGenerated")} — ${segments.length} segments`, "success");

            if (!montage.montage_id) {
                try {
                    const createRes = await fetch("/api/fast/montage/create", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            project_id: activeProjectId || null,
                            title: montage.title || t("montage.untitled"),
                            description: montage.description,
                            aspect_ratio: montage.aspect_ratio,
                            resolution: montage.resolution
                        })
                    });
                    const createData = await createRes.json();
                    if (createData.ok && createData.montage) {
                        const created = {
                            ...createData.montage,
                            json_data: { ...createData.montage.json_data, segments, total_duration: totalDuration }
                        };
                        setMontage(created);
                        updatedMontage = created;
                        lastSavedRef.current = "";
                        await saveMontageSnapshot(created, false);
                        if (activeProjectId) {
                            const listRes = await fetch(`/api/fast/montage/list?project_id=${encodeURIComponent(activeProjectId)}`);
                            const listData = await listRes.json();
                            setMontagesList(listData.montages || []);
                        }
                    }
                } catch {
                    // creation failed, storyboard still in memory
                }
            } else {
                await saveMontageSnapshot(updatedMontage || montage, false);
            }
        } catch (err) {
            toast(err.message || t("montage.err.storyboard"), "error");
        } finally {
            setLoading(false);
        }
    }, [montage, activeProjectId, saveMontageSnapshot, toast]);

    const handleGenerateImages = useCallback(async (segmentIdsOverride = null, targets = [], selectedSegments = []) => {
        if (!montage.montage_id) {
            setError(t("montage.err.notCreated"));
            return;
        }

        const segmentIds = segmentIdsOverride && segmentIdsOverride.length > 0
            ? segmentIdsOverride
            : (selectedSegments.length > 0
                ? selectedSegments
                : montage.json_data.segments.map(s => s.segment_id));

        if (segmentIds.length === 0) {
            setError(t("montage.err.noSegment"));
            return;
        }

        setGeneratingImages(true);
        setError("");

        try {
            const res = await fetch("/api/fast/montage/images/generate", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    montage_id: montage.montage_id,
                    segment_ids: segmentIds,
                    quality: imageQuality,
                    aspect_ratio: montage.aspect_ratio,
                    targets
                })
            });

            const data = await res.json();
            if (!data.ok) throw new Error(data.error || t("montage.err.genImages"));

            setMontage(data.montage);
            lastSavedRef.current = JSON.stringify(data.montage);
            toast(t("montage.toast.imagesGen"), "success");
        } catch (err) {
            toast(err.message || t("montage.err.genImages"), "error");
        } finally {
            setGeneratingImages(false);
        }
    }, [montage.montage_id, imageQuality, montage.json_data.segments, montage.aspect_ratio, toast]);

    const handleGenerateVideos = useCallback(async (segmentIdsOverride = null, selectedVideoSegments = []) => {
        if (!montage.montage_id) {
            setError(t("montage.err.notCreated"));
            return;
        }

        const segmentIds = segmentIdsOverride && segmentIdsOverride.length > 0
            ? segmentIdsOverride
            : (selectedVideoSegments.length > 0
                ? selectedVideoSegments
                : montage.json_data.segments.map(s => s.segment_id));

        if (segmentIds.length === 0) {
            setError(t("montage.err.noSegment"));
            return;
        }

        setGeneratingVideos(true);
        setError("");

        try {
            const jobPayload = {
                montage_id: montage.montage_id,
                segment_ids: segmentIds,
                quality: videoQuality,
                aspect_ratio: montage.aspect_ratio
            };
            const jobRes = await fetch("/api/fast/jobs", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ gen_type: "montage_video", payload: jobPayload })
            });
            if (!jobRes.ok) {
                const err = await jobRes.json().catch(() => ({}));
                throw new Error(err.detail || t("montage.err.genVideos"));
            }
            const { job_id: jobId } = await jobRes.json();
            let data = null;
            for (let attempt = 0; attempt < 200; attempt++) {
                await new Promise(r => setTimeout(r, 3000));
                const poll = await (await fetch(`/api/fast/jobs/${jobId}`)).json();
                if (poll.status === "done") { data = poll.result; break; }
                if (poll.status === "error") throw new Error(poll.error || t("montage.err.genVideos"));
            }
            if (!data || !data.ok) throw new Error((data && data.error) || t("montage.err.genVideos"));

            setMontage(data.montage);
            lastSavedRef.current = JSON.stringify(data.montage);
            toast(t("montage.toast.videosGen"), "success");
        } catch (err) {
            toast(err.message || t("montage.err.genVideos"), "error");
        } finally {
            setGeneratingVideos(false);
        }
    }, [montage.montage_id, videoQuality, montage.json_data.segments, montage.aspect_ratio, toast]);

    const handleExport = useCallback(async () => {
        if (!montage.montage_id) return;
        setExporting(true);
        setError("");
        try {
            const res = await fetch(`/api/fast/montage/${montage.montage_id}/export`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
            });
            const data = await res.json();
            if (!data.ok) throw new Error(data.error || t("montage.err.export"));

            setMontage(data.montage);
            lastSavedRef.current = JSON.stringify(data.montage);
            toast(t("montage.toast.exportStarted"), "success");
        } catch (err) {
            toast(err.message || t("montage.err.export"), "error");
        } finally {
            setExporting(false);
        }
    }, [montage.montage_id, toast]);

    const handleHarmonize = useCallback(async () => {
        if (!montage.montage_id) return;

        const stylePrompt = prompt(
            "Décrivez le style à appliquer à toutes les vidéos (ex: 'Appliquer un color grading cinématique chaud', 'Style anime vibrant', 'Noir et blanc contrasté'):"
        );
        if (!stylePrompt) return;

        setHarmonizing(true);
        setError("");
        try {
            const res = await fetch("/api/fast/montage/videos/edit", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    montage_id: montage.montage_id,
                    style_prompt: stylePrompt,
                }),
            });
            const data = await res.json();
            if (!data.ok) throw new Error(data.error || t("montage.err.harmonize"));

            setMontage(data.montage);
            lastSavedRef.current = JSON.stringify(data.montage);
            toast(t("montage.toast.harmonized"), "success");
        } catch (err) {
            toast(err.message || t("montage.err.harmonize"), "error");
        } finally {
            setHarmonizing(false);
        }
    }, [montage.montage_id, toast]);

    const handleRegenerateSegment = useCallback(async (index) => {
        const segment = montage.json_data.segments[index];
        if (!segment) return;

        setRegeneratingSegment(index);
        setError("");

        try {
            const res = await fetch("/api/fast/montage/segment/regenerate", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    montage_id: montage.montage_id,
                    segment_id: segment.segment_id,
                    context: montage.description,
                    target_duration: segment.duration
                })
            });

            const data = await res.json();
            if (!data.ok) throw new Error(data.error || t("montage.err.regenerate"));

            if (data.segment) {
                handleUpdateSegment(index, data.segment);
            }
        } catch (err) {
            setError(err.message || t("montage.err.regenerate"));
        } finally {
            setRegeneratingSegment(null);
        }
    }, [montage.montage_id, montage.description, montage.json_data.segments, handleUpdateSegment]);

    const handleEnhanceSegmentPrompt = useCallback(async (index) => {
        const segment = montage.json_data.segments[index];
        if (!segment) return;

        const promptText = typeof segment.prompt === "string"
            ? segment.prompt
            : segment.prompt?.text || "";

        const startImagePrompt = segment.start_image?.prompt || "";
        const endImagePrompt = segment.end_image?.prompt || "";

        if (!promptText && !startImagePrompt && !endImagePrompt) return;

        setError("");

        try {
            const prompts = [promptText, startImagePrompt, endImagePrompt].filter(Boolean);
            const promptMap = [];
            if (promptText) promptMap.push("main");
            if (startImagePrompt) promptMap.push("start_image");
            if (endImagePrompt) promptMap.push("end_image");

            const res = await fetch("/api/fast/montage/prompts/enhance", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ prompts, context: montage.description })
            });

            const data = await res.json();
            if (!data.ok) throw new Error(data.error || t("montage.err.enhance"));

            if (data.enhanced_prompts && data.enhanced_prompts.length === prompts.length) {
                const updatedSegment = { ...segment };

                promptMap.forEach((type, i) => {
                    if (type === "main") {
                        updatedSegment.prompt = typeof segment.prompt === "string"
                            ? { text: segment.prompt, enhanced: data.enhanced_prompts[i], tags: [] }
                            : { ...segment.prompt, enhanced: data.enhanced_prompts[i] };
                    } else if (type === "start_image") {
                        updatedSegment.start_image = { ...segment.start_image, prompt: data.enhanced_prompts[i] };
                    } else if (type === "end_image") {
                        updatedSegment.end_image = { ...segment.end_image, prompt: data.enhanced_prompts[i] };
                    }
                });

                handleUpdateSegment(index, updatedSegment);
            }
        } catch (err) {
            setError(err.message || t("montage.err.enhance"));
        }
    }, [montage.description, montage.json_data.segments, handleUpdateSegment]);

    const handleEnhanceAllPrompts = useCallback(async () => {
        const segments = montage.json_data.segments;
        if (!segments || segments.length === 0) return;

        const allPrompts = [];
        const promptMapping = [];

        segments.forEach((seg, idx) => {
            const mainPrompt = typeof seg.prompt === "string" ? seg.prompt : seg.prompt?.text || "";
            const startPrompt = seg.start_image?.prompt || "";
            const endPrompt = seg.end_image?.prompt || "";

            if (mainPrompt) { allPrompts.push(mainPrompt); promptMapping.push({ segIndex: idx, type: "main" }); }
            if (startPrompt) { allPrompts.push(startPrompt); promptMapping.push({ segIndex: idx, type: "start_image" }); }
            if (endPrompt) { allPrompts.push(endPrompt); promptMapping.push({ segIndex: idx, type: "end_image" }); }
        });

        if (allPrompts.length === 0) return;

        setEnhancingPrompts(true);
        setError("");

        try {
            const res = await fetch("/api/fast/montage/prompts/enhance", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ prompts: allPrompts, context: montage.description })
            });

            const data = await res.json();
            if (!data.ok) throw new Error(data.error || t("montage.err.enhance"));

            if (data.enhanced_prompts && data.enhanced_prompts.length === allPrompts.length) {
                const updatedSegments = segments.map(seg => ({ ...seg }));

                promptMapping.forEach((mapping, i) => {
                    const seg = updatedSegments[mapping.segIndex];
                    if (mapping.type === "main") {
                        seg.prompt = typeof seg.prompt === "string"
                            ? { text: seg.prompt, enhanced: data.enhanced_prompts[i], tags: [] }
                            : { ...seg.prompt, enhanced: data.enhanced_prompts[i] };
                    } else if (mapping.type === "start_image") {
                        seg.start_image = { ...seg.start_image, prompt: data.enhanced_prompts[i] };
                    } else if (mapping.type === "end_image") {
                        seg.end_image = { ...seg.end_image, prompt: data.enhanced_prompts[i] };
                    }
                });

                setMontage(prev => ({
                    ...prev,
                    json_data: { ...prev.json_data, segments: updatedSegments }
                }));
                toast(t("montage.toast.enhanced"), "success");
            }
        } catch (err) {
            toast(err.message || t("montage.err.enhance"), "error");
        } finally {
            setEnhancingPrompts(false);
        }
    }, [montage.description, montage.json_data.segments, toast]);

    // ──────────────────────────────────────────────────
    // Storyboard drag & drop
    // ──────────────────────────────────────────────────

    const handleStoryboardDragStart = useCallback((e, index) => {
        e.dataTransfer.effectAllowed = "move";
        setDraggedSegmentIndex(index);
    }, []);

    const handleStoryboardDragOver = useCallback((e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (draggedSegmentIndex !== index) {
            setDragOverIndex(index);
        }
    }, [draggedSegmentIndex]);

    const handleStoryboardDragLeave = useCallback(() => {
        setDragOverIndex(null);
    }, []);

    const handleStoryboardDrop = useCallback((e, dropIndex) => {
        e.preventDefault();
        if (draggedSegmentIndex === null || draggedSegmentIndex === dropIndex) {
            setDraggedSegmentIndex(null);
            setDragOverIndex(null);
            return;
        }

        setMontage(prev => {
            const newSegments = [...prev.json_data.segments];
            const [movedSegment] = newSegments.splice(draggedSegmentIndex, 1);
            newSegments.splice(dropIndex, 0, movedSegment);
            newSegments.forEach((seg, idx) => { seg.order = idx + 1; });

            return {
                ...prev,
                json_data: { ...prev.json_data, segments: newSegments }
            };
        });

        setDraggedSegmentIndex(null);
        setDragOverIndex(null);
    }, [draggedSegmentIndex]);

    const handleStoryboardDragEnd = useCallback(() => {
        setDraggedSegmentIndex(null);
        setDragOverIndex(null);
    }, []);

    // ──────────────────────────────────────────────────
    // Reset helper
    // ──────────────────────────────────────────────────

    const resetMontage = useCallback(() => {
        setMontage(INITIAL_MONTAGE);
        setCurrentStep("storyboard");
        setMontageIdInUrl(null);
    }, []);

    return {
        // State
        montage,
        setMontage,
        loading,
        saving,
        error,
        setError,
        currentStep,
        setCurrentStep,
        activeProjectId,
        montagesList,
        loadingList,

        // Generation state
        imageQuality,
        setImageQuality,
        generatingImages,
        videoQuality,
        setVideoQuality,
        generatingVideos,
        exporting,
        harmonizing,
        regeneratingSegment,
        enhancingPrompts,

        // Drag & drop state
        draggedSegmentIndex,
        dragOverIndex,

        // Refs
        lastSavedRef,

        // CRUD
        handleCreate,
        handleSave,
        handleLoad,
        handleDelete,
        resetMontage,
        saveMontageSnapshot,

        // Updaters
        updateMontage,
        handleReferenceChange,
        handleAddReferenceFromSegment,
        updateJsonData,

        // Segment manipulation
        handleUpdateSegment,
        handleSaveSegment,
        handleDeleteSegment,
        handleAddSegment,

        // Generation
        handleGenerateStoryboard,
        handleGenerateImages,
        handleGenerateVideos,
        handleExport,
        handleHarmonize,
        handleRegenerateSegment,
        handleEnhanceSegmentPrompt,
        handleEnhanceAllPrompts,

        // Drag & drop
        handleStoryboardDragStart,
        handleStoryboardDragOver,
        handleStoryboardDragLeave,
        handleStoryboardDrop,
        handleStoryboardDragEnd,
    };
}

export { INITIAL_MONTAGE };
